import type { WindowSummary } from './types';

/** Per target: 60 windows of 10 s = ten minutes of buffer for each target. */
export const LINE_PROBE_QUEUE_LIMIT_PER_TARGET = 60;
export const LINE_PROBE_BATCH_SIZE = 12;

/** Wire format of LiveWebinarHub.LineProbeStats and POST /api/test-suite/line-probe/stats. */
export interface LineProbeStatsBatch {
    sentAt: number;
    sessionId: string;
    windows: WindowSummary[];
}

export type LineProbeStatsSender = (batch: LineProbeStatsBatch) => Promise<{ success: boolean; statusCode?: number }>;

/**
 * Holds sealed 10-second windows until they can be sent, same semantics as the player stats queue:
 * one send in flight, a window is removed only once the server answered for its batch, a 4xx answer
 * (it will never succeed) drops the batch, an exception or 5xx keeps it for the next flush. Windows
 * keep their own windowStart, so a hub reconnect after minutes still delivers them unchanged. The
 * session id is read at send time: nothing is sent while the runner has none.
 */
export class LineProbeStatsQueue {
    private windows: WindowSummary[] = [];
    private inFlight = false;

    constructor(
        private readonly send: LineProbeStatsSender,
        private readonly isConnected: () => boolean,
        private readonly sessionId: () => string | undefined,
        private readonly now: () => number = Date.now
    ) {}

    get size(): number {
        return this.windows.length;
    }

    clear(): void {
        this.windows = [];
    }

    push(window: WindowSummary): void {
        this.windows.push(window);
        // Drop the oldest window of this target only, so a busy target never evicts the other one.
        let count = 0;
        for (const w of this.windows) if (w.target === window.target) count++;
        if (count > LINE_PROBE_QUEUE_LIMIT_PER_TARGET) {
            const oldest = this.windows.findIndex((w) => w.target === window.target);
            this.windows.splice(oldest, 1);
        }
        void this.flush();
    }

    async flush(): Promise<void> {
        const sessionId = this.sessionId();
        if (this.inFlight || this.windows.length === 0 || !sessionId || !this.isConnected()) {
            return;
        }

        const batch = this.windows.slice(0, LINE_PROBE_BATCH_SIZE);
        this.inFlight = true;
        let accepted = false;
        try {
            const response = await this.send({ sentAt: this.now(), sessionId, windows: batch });
            const rejected =
                !response.success &&
                response.statusCode !== undefined &&
                response.statusCode >= 400 &&
                response.statusCode < 500;
            accepted = response.success || rejected;
            if (accepted) {
                this.windows = this.windows.filter((w) => !batch.includes(w));
            }
        } catch {
            // Keep the windows for the next flush
        } finally {
            this.inFlight = false;
        }

        if (accepted && this.windows.length > 0) {
            void this.flush();
        }
    }
}
